import {Link} from '@shopify/hydrogen';
import clsx from 'clsx';
import type {ButtonHTMLAttributes, ElementType} from 'react';
import {twMerge} from 'tailwind-merge';

type ButtonMode = 'primary' | 'secondary' | 'outline';
type ButtonTone = 'critical' | 'default' | 'inverted' | 'shopPay';

type Props = {
  as?: ElementType;
  className?: string;
  mode?: ButtonMode;
  onClick?: () => void;
  to?: string;
  tone?: ButtonTone;
} & ButtonHTMLAttributes<HTMLButtonElement>;

type ButtonStyleOptions = {
  mode?: ButtonMode;
  tone?: ButtonTone;
};

export const defaultButtonStyles = (options?: ButtonStyleOptions) => {
  const mode: ButtonMode = options?.mode || 'primary';
  const tone: ButtonTone = options?.tone || 'default';

  return clsx([
    'flex items-center justify-center overflow-hidden rounded-sm px-4 pt-2 pb-[7px] text-xs font-medium duration-300 ease-out',
    'disabled:opacity-20 disabled:bg-opacity-100',
    mode === 'primary' &&
      clsx([
        tone === 'critical' && 'bg-red text-white',
        tone === 'default' && 'bg-blue text-white',
        tone === 'inverted' && 'bg-white text-blue',
        tone === 'shopPay' && 'bg-shopPay text-white',
        'md:hover:rounded-xl uppercase',
      ]),
    mode === 'secondary' &&
      clsx([
        'bg-black text-white rounded-lg px-[18px] py-[6px]',
        'md:hover:bg-gray',
      ]),
    mode === 'outline' &&
      clsx([
        tone === 'critical' && 'border-red text-red',
        tone === 'default' && 'border-black text-black',
        tone === 'shopPay' && 'border-shopPay text-shopPay',
        'border md:hover:border-gray md:hover:bg-gray md:hover:text-white rounded-xl',
      ]),
  ]);
};

export default function Button({
  as = 'button',
  className,
  mode = 'primary',
  tone,
  ...props
}: Props) {
  const Component = props?.to ? Link : as;

  return (
    <Component
      className={twMerge(defaultButtonStyles({mode, tone}), className)}
      {...props}
    />
  );
}
